/**
 * Send Notification Component
 *
 * Panel for sending test/manual notifications.
 * Allows selecting event, channel, recipient and payload.
 */

import React, { useEffect, useRef, useState } from "react";
import { AppCard } from "../../components/ui";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Tag } from "primereact/tag";
import { Divider } from "primereact/divider";
import { RadioButton } from "primereact/radiobutton";
import { storageService } from "../../services/StorageService";
import {
    notificationsApiService,
    eventDefinitionsApiService,
    languagesApiService,
    topicsApiService,
} from "../../services/api/notifications";
import {
    EventDefinitionDto,
    LanguageDto,
    NotificationTopicDto,
    NotificationChannel,
    NotificationChannelLabels,
    DeliveryStatus,
    DeliveryStatusLabels,
    parseAllowedChannels,
    getDeliveryStatusSeverity,
    SendNotificationResult,
    SendToTopicResult,
    RenderResult,
} from "../../models/notifications";

interface SendNotificationProps {
    merchantId?: number;
}

const SendNotification: React.FC<SendNotificationProps> = ({ merchantId: propMerchantId }) => {
    const toast = useRef<Toast>(null);

    const merchantId = propMerchantId ?? Number(storageService.getSelectedMerchantId());

    const [events, setEvents] = useState<EventDefinitionDto[]>([]);
    const [languages, setLanguages] = useState<LanguageDto[]>([]);
    const [topics, setTopics] = useState<NotificationTopicDto[]>([]);
    const [allowedChannels, setAllowedChannels] = useState<NotificationChannel[]>([]);

    // Audience type
    type AudienceType = "individual" | "topic";
    const [audienceType, setAudienceType] = useState<AudienceType>("individual");

    // Form state
    const [eventCode, setEventCode] = useState<string>("");
    const [channel, setChannel] = useState<NotificationChannel | null>(null);
    const [userId, setUserId] = useState("");
    const [recipientEmail, setRecipientEmail] = useState("");
    const [recipientPhone, setRecipientPhone] = useState("");
    const [selectedTopicId, setSelectedTopicId] = useState<string>("");
    const [languageIso, setLanguageIso] = useState<string>("");
    const [payloadJson, setPayloadJson] = useState("{}");

    // Result state
    const [isSending, setIsSending] = useState(false);
    const [isPreviewing, setIsPreviewing] = useState(false);
    const [sendResult, setSendResult] = useState<SendNotificationResult | SendToTopicResult | null>(null);
    const [previewResult, setPreviewResult] = useState<RenderResult | null>(null);
    const [errors, setErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        loadInitialData();
    }, []);

    useEffect(() => {
        if (eventCode && events.length > 0) {
            const event = events.find((e) => e.eventCode === eventCode);
            if (event) {
                const channels = parseAllowedChannels(event.allowedChannels);
                setAllowedChannels(channels);
                if (!channel || !channels.includes(channel)) {
                    setChannel(channels[0] ?? null);
                }
            }
        }
    }, [eventCode, events]);

    // Reset to individual when switching away from Push (only Push supports topics)
    useEffect(() => {
        if (channel !== NotificationChannel.Push && audienceType === "topic") {
            setAudienceType("individual");
        }
    }, [channel]);

    const getDefaultLanguageIso = (langs: { isoCode: string }[]) => {
        if (langs.length === 1) return langs[0].isoCode;
        return langs.find((l) => l.isoCode === "en" || l.isoCode === "en-US")?.isoCode ?? "";
    };

    const loadInitialData = async () => {
        const [eventsResult, languagesResult, topicsResult] = await Promise.all([
            eventDefinitionsApiService.getActive(merchantId).catch(() => null),
            languagesApiService.getActive(merchantId).catch(() => null),
            topicsApiService.getAll(merchantId).catch(() => null),
        ]);

        if (eventsResult?.success) setEvents(eventsResult.data ?? []);

        if (languagesResult?.success) {
            const langList = languagesResult.data ?? [];
            setLanguages(langList);
            setLanguageIso(getDefaultLanguageIso(langList));
        }

        if (topicsResult?.success) setTopics(topicsResult.data ?? []);
    };

    const parsePayload = (): Record<string, unknown> | null => {
        try {
            return JSON.parse(payloadJson);
        } catch {
            return null;
        }
    };

    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!eventCode) {
            newErrors.eventCode = "Event is required";
        }
        if (channel === null) {
            newErrors.channel = "Channel is required";
        }

        if (audienceType === "individual") {
            if (channel === NotificationChannel.Email) {
                if (!recipientEmail.trim()) {
                    newErrors.recipientEmail = "Recipient email is required";
                }
            } else if (channel === NotificationChannel.Sms) {
                if (!recipientPhone.trim()) {
                    newErrors.recipientPhone = "Recipient phone is required";
                }
            } else {
                if (!userId.trim()) {
                    newErrors.userId = "User ID is required";
                }
            }
        } else {
            if (!selectedTopicId) {
                newErrors.topicId = "Topic is required";
            }
        }

        const payload = parsePayload();
        if (payload === null) {
            newErrors.payloadJson = "Invalid JSON format";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handlePreview = async () => {
        if (!eventCode || channel === null) {
            toast.current?.show({
                severity: "warn",
                summary: "Missing Fields",
                detail: "Please select an event and channel first",
                life: 3000,
            });
            return;
        }

        const payload = parsePayload();
        if (payload === null) {
            toast.current?.show({
                severity: "error",
                summary: "Invalid JSON",
                detail: "Payload must be valid JSON",
                life: 3000,
            });
            return;
        }

        setIsPreviewing(true);
        setPreviewResult(null);

        const result = await notificationsApiService.previewExisting(
            merchantId,
            eventCode,
            channel,
            payload,
            undefined,
            languageIso || undefined
        );

        if (result.success) {
            setPreviewResult(result.data);
        } else {
            toast.current?.show({
                severity: "error",
                summary: "Preview Failed",
                detail: result.message || "Failed to render preview",
                life: 4000,
            });
        }

        setIsPreviewing(false);
    };

    const handleSend = async () => {
        if (!validate()) return;
        if (channel === null) return;

        const payload = parsePayload();
        if (payload === null) return;

        setIsSending(true);
        setSendResult(null);

        if (audienceType === "topic") {
            const result = await notificationsApiService.sendToTopic({
                merchantId,
                topicId: selectedTopicId,
                eventCode,
                channel,
                templatePayload: payload as Record<string, string>,
                languageIso: languageIso || undefined,
            });

            if (result.success) {
                setSendResult(result.data);
                toast.current?.show({
                    severity: result.data.status === DeliveryStatus.Sent ? "success" : "info",
                    summary: "Topic Notification Sent",
                    detail: `Sent to topic: ${result.data.topicName}`,
                    life: 5000,
                });
            } else {
                toast.current?.show({
                    severity: "error",
                    summary: "Send Failed",
                    detail: result.message || "Failed to send topic notification",
                    life: 4000,
                });
            }
        } else {
            const result = await notificationsApiService.send({
                merchantId,
                eventCode,
                channel,
                userId: userId || undefined,
                recipientEmail: recipientEmail || undefined,
                recipientPhone: recipientPhone || undefined,
                templatePayload: payload,
                languageIso: languageIso || undefined,
            });

            if (result.success) {
                setSendResult(result.data);
                toast.current?.show({
                    severity: result.data.status === DeliveryStatus.Sent ? "success" : "info",
                    summary: "Notification Sent",
                    detail: `Delivery ID: ${result.data.deliveryId}`,
                    life: 5000,
                });
            } else {
                toast.current?.show({
                    severity: "error",
                    summary: "Send Failed",
                    detail: result.message || "Failed to send notification",
                    life: 4000,
                });
            }
        }

        setIsSending(false);
    };

    const handleReset = () => {
        setAudienceType("individual");
        setEventCode("");
        setChannel(null);
        setUserId("");
        setRecipientEmail("");
        setRecipientPhone("");
        setSelectedTopicId("");
        setLanguageIso(getDefaultLanguageIso(languages));
        setPayloadJson("{}");
        setSendResult(null);
        setPreviewResult(null);
        setErrors({});
    };

    // Options
    const eventOptions = events.map((e) => ({
        label: e.eventCode,
        value: e.eventCode,
    }));

    const channelOptions = allowedChannels.map((ch) => ({
        label: NotificationChannelLabels[ch],
        value: ch,
    }));

    const languageOptions = [
        { label: "Default", value: "" },
        ...languages.map((l) => ({
            label: `${l.name} (${l.isoCode})`,
            value: l.isoCode,
        })),
    ];

    // Build grouped topic options
    const topicOptions = (() => {
        const systemTopics = topics.filter((t) => t.isSystem);
        const customTopics = topics.filter((t) => !t.isSystem);
        const options: { label: string; value: string; isGroupHeader?: boolean; disabled?: boolean }[] = [];

        if (systemTopics.length > 0) {
            options.push({ label: "System Topics", value: "__system_header__", isGroupHeader: true, disabled: true });
            systemTopics.forEach((t) => options.push({ label: `${t.displayName}`, value: t.topicId }));
        }
        if (customTopics.length > 0) {
            options.push({ label: "Custom Topics", value: "__custom_header__", isGroupHeader: true, disabled: true });
            customTopics.forEach((t) => options.push({ label: `${t.displayName}`, value: t.topicId }));
        }
        return options;
    })();

    const topicItemTemplate = (option: { label: string; value: string; isGroupHeader?: boolean }) => {
        if (option.isGroupHeader) {
            return <span className="text-xs font-bold text-gray-500 uppercase">{option.label}</span>;
        }
        const topic = topics.find((t) => t.topicId === option.value);
        return (
            <div>
                <div>{option.label}</div>
                {topic && <small className="text-gray-400 font-mono">{topic.fcmTopicName}</small>}
            </div>
        );
    };

    const selectedEvent = events.find((e) => e.eventCode === eventCode);

    return (
        <>
            <Toast ref={toast} position="bottom-center" />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Send Form */}
                <AppCard>
                    <AppCard.Header>
                        <div className="flex flex-col gap-1">
                            <span>Send Notification</span>
                            <small className="text-neutral-500 font-normal">
                                Send a notification to an individual user or a topic audience.
                            </small>
                        </div>
                    </AppCard.Header>
                    <AppCard.Body>
                        <div className="space-y-4">
                            {/* Event Selection */}
                            <div>
                                <label className="block text-sm font-semibold mb-1">
                                    Event <span className="text-red-500">*</span>
                                </label>
                                <Dropdown
                                    value={eventCode}
                                    options={eventOptions}
                                    onChange={(e) => setEventCode(e.value)}
                                    placeholder="Select Event"
                                    className={`w-full ${errors.eventCode ? "p-invalid" : ""}`}
                                    filter
                                />
                                {errors.eventCode && (
                                    <small className="p-error">{errors.eventCode}</small>
                                )}
                            </div>

                            {/* Channel Selection */}
                            <div>
                                <label className="block text-sm font-semibold mb-1">
                                    Channel <span className="text-red-500">*</span>
                                </label>
                                <Dropdown
                                    value={channel}
                                    options={channelOptions}
                                    onChange={(e) => setChannel(e.value)}
                                    placeholder="Select Channel"
                                    className={`w-full ${errors.channel ? "p-invalid" : ""}`}
                                    disabled={!eventCode}
                                />
                                {errors.channel && (
                                    <small className="p-error">{errors.channel}</small>
                                )}
                            </div>

                            {/* Recipient */}
                            <div>
                                <label className="block text-sm font-semibold mb-2">
                                    {channel === NotificationChannel.Email
                                        ? "Recipient Email"
                                        : channel === NotificationChannel.Sms
                                        ? "Recipient Phone"
                                        : "Recipient"}{" "}
                                    <span className="text-red-500">*</span>
                                </label>

                                {/* Push supports both individual and topic */}
                                {channel === NotificationChannel.Push && (
                                    <div className="space-y-3">
                                        <div className="flex items-start gap-2">
                                            <RadioButton
                                                inputId="audience-individual"
                                                value="individual"
                                                checked={audienceType === "individual"}
                                                onChange={() => setAudienceType("individual")}
                                            />
                                            <div className="flex-1">
                                                <label htmlFor="audience-individual" className="text-sm font-medium cursor-pointer">
                                                    Individual User
                                                </label>
                                                {audienceType === "individual" && (
                                                    <div className="mt-2">
                                                        <InputText
                                                            value={userId}
                                                            onChange={(e) => setUserId(e.target.value)}
                                                            placeholder="Enter recipient user ID"
                                                            className={`w-full ${errors.userId ? "p-invalid" : ""}`}
                                                        />
                                                        {errors.userId && (
                                                            <small className="p-error">{errors.userId}</small>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="flex items-start gap-2">
                                            <RadioButton
                                                inputId="audience-topic"
                                                value="topic"
                                                checked={audienceType === "topic"}
                                                onChange={() => setAudienceType("topic")}
                                            />
                                            <div className="flex-1">
                                                <label htmlFor="audience-topic" className="text-sm font-medium cursor-pointer">
                                                    Topic (broadcast)
                                                </label>
                                                {audienceType === "topic" && (
                                                    <div className="mt-2">
                                                        <Dropdown
                                                            value={selectedTopicId}
                                                            options={topicOptions}
                                                            onChange={(e) => setSelectedTopicId(e.value)}
                                                            placeholder="Select topic..."
                                                            className={`w-full ${errors.topicId ? "p-invalid" : ""}`}
                                                            itemTemplate={topicItemTemplate}
                                                            filter
                                                            filterPlaceholder="Search topics..."
                                                        />
                                                        {errors.topicId && (
                                                            <small className="p-error">{errors.topicId}</small>
                                                        )}
                                                        {selectedTopicId && (
                                                            <small className="text-gray-500 block mt-1">
                                                                FCM topic: <code className="text-blue-600 font-mono">
                                                                    {topics.find((t) => t.topicId === selectedTopicId)?.fcmTopicName}
                                                                </code>
                                                            </small>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Email — recipient email */}
                                {channel === NotificationChannel.Email && (
                                    <div>
                                        <InputText
                                            value={recipientEmail}
                                            onChange={(e) => setRecipientEmail(e.target.value)}
                                            placeholder="Enter recipient email address"
                                            className={`w-full ${errors.recipientEmail ? "p-invalid" : ""}`}
                                        />
                                        {errors.recipientEmail && (
                                            <small className="p-error">{errors.recipientEmail}</small>
                                        )}
                                    </div>
                                )}

                                {/* SMS — recipient phone */}
                                {channel === NotificationChannel.Sms && (
                                    <div>
                                        <InputText
                                            value={recipientPhone}
                                            onChange={(e) => setRecipientPhone(e.target.value)}
                                            placeholder="Enter recipient phone number"
                                            className={`w-full ${errors.recipientPhone ? "p-invalid" : ""}`}
                                        />
                                        {errors.recipientPhone && (
                                            <small className="p-error">{errors.recipientPhone}</small>
                                        )}
                                    </div>
                                )}

                                {/* Pull — user ID only */}
                                {channel === NotificationChannel.Pull && (
                                    <div>
                                        <InputText
                                            value={userId}
                                            onChange={(e) => setUserId(e.target.value)}
                                            placeholder="Enter recipient user ID"
                                            className={`w-full ${errors.userId ? "p-invalid" : ""}`}
                                        />
                                        {errors.userId && (
                                            <small className="p-error">{errors.userId}</small>
                                        )}
                                    </div>
                                )}

                                {/* No channel selected yet */}
                                {channel === null && (
                                    <small className="text-gray-400">Select a channel first</small>
                                )}
                            </div>

                            {/* Language */}
                            <div>
                                <label className="block text-sm font-semibold mb-1">Language</label>
                                <Dropdown
                                    value={languageIso}
                                    options={languageOptions}
                                    onChange={(e) => setLanguageIso(e.value)}
                                    placeholder="Select Language"
                                    className="w-full"
                                />
                            </div>

                            {/* Payload */}
                            <div>
                                <label className="block text-sm font-semibold mb-1">
                                    Payload (JSON)
                                </label>
                                <InputTextarea
                                    value={payloadJson}
                                    onChange={(e) => setPayloadJson(e.target.value)}
                                    rows={5}
                                    className={`w-full font-mono text-sm ${errors.payloadJson ? "p-invalid" : ""}`}
                                    placeholder='{"amount": "50.00", "currency": "BGN"}'
                                />
                                {errors.payloadJson && (
                                    <small className="p-error">{errors.payloadJson}</small>
                                )}

                                {/* Show allowed variables */}
                                {selectedEvent?.allowedVariables && selectedEvent.allowedVariables.length > 0 && (
                                    <div className="mt-2">
                                        <small className="text-gray-600">Expected variables: </small>
                                        <div className="flex flex-wrap gap-1 mt-1">
                                            {selectedEvent.allowedVariables.map((v) => (
                                                <Tag
                                                    key={v}
                                                    value={v}
                                                    className="text-xs bg-blue-100 text-blue-700"
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <Divider />

                            {/* Actions */}
                            <div className="flex gap-2">
                                <Button
                                    size="small"
                                    label="Preview"
                                    icon="pi pi-eye"
                                    outlined
                                    onClick={handlePreview}
                                    loading={isPreviewing}
                                    disabled={!eventCode || channel === null}
                                />
                                <Button
                                    size="small"
                                    label="Send"
                                    icon="pi pi-send"
                                    onClick={handleSend}
                                    loading={isSending}
                                />
                                <Button
                                    size="small"
                                    label="Reset"
                                    icon="pi pi-refresh"
                                    outlined
                                    severity="secondary"
                                    onClick={handleReset}
                                />
                            </div>
                        </div>
                    </AppCard.Body>
                </AppCard>

                {/* Results Panel */}
                <div className="space-y-4">
                    {/* Preview Result */}
                    {previewResult && (
                        <AppCard>
                            <AppCard.Header title="Preview Result" />
                            <AppCard.Body>
                                <div className="space-y-3">
                                    {previewResult.renderedSubject && (
                                        <div>
                                            <span className="text-sm text-gray-600 font-semibold">Subject:</span>
                                            <div className="p-2 bg-slate-50 rounded mt-1">
                                                {previewResult.renderedSubject}
                                            </div>
                                        </div>
                                    )}
                                    {previewResult.renderedTitle && (
                                        <div>
                                            <span className="text-sm text-gray-600 font-semibold">Title:</span>
                                            <div className="p-2 bg-slate-50 rounded mt-1">
                                                {previewResult.renderedTitle}
                                            </div>
                                        </div>
                                    )}
                                    <div>
                                        <span className="text-sm text-gray-600 font-semibold">Body:</span>
                                        <pre className="p-3 bg-slate-50 rounded mt-1 whitespace-pre-wrap text-sm">
                                            {previewResult.renderedBody}
                                        </pre>
                                    </div>

                                    {previewResult.renderedDeepLink && (
                                        <div>
                                            <span className="text-sm text-gray-600 font-semibold">Deep Link:</span>
                                            <div className="p-2 bg-slate-50 rounded mt-1">
                                                <code className="font-mono text-sm text-blue-600">
                                                    {previewResult.renderedDeepLink}
                                                </code>
                                            </div>
                                        </div>
                                    )}

                                    {previewResult.unresolvedPlaceholders.length > 0 && (
                                        <div className="p-2 bg-amber-50 rounded border border-amber-200">
                                            <span className="text-sm font-semibold text-amber-700">
                                                Unresolved Placeholders:
                                            </span>
                                            <div className="flex flex-wrap gap-1 mt-1">
                                                {previewResult.unresolvedPlaceholders.map((p) => (
                                                    <Tag key={p} value={p} severity="warning" className="text-xs" />
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {previewResult.warnings.length > 0 && (
                                        <div className="p-2 bg-amber-50 rounded border border-amber-200">
                                            <span className="text-sm font-semibold text-amber-700">Warnings:</span>
                                            <ul className="text-sm text-amber-700 mt-1 list-disc list-inside">
                                                {previewResult.warnings.map((w, i) => (
                                                    <li key={i}>{w}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            </AppCard.Body>
                        </AppCard>
                    )}

                    {/* Send Result */}
                    {sendResult && (
                        <AppCard>
                            <AppCard.Header title="Send Result" actions={
                                <Tag
                                    value={DeliveryStatusLabels[sendResult.status]}
                                    severity={getDeliveryStatusSeverity(sendResult.status)}
                                />
                            } />
                            <AppCard.Body>
                                <div className="space-y-2">
                                    <div>
                                        <span className="text-sm text-gray-600 font-semibold">Delivery ID:</span>
                                        <div className="font-mono text-sm bg-slate-50 p-2 rounded mt-1">
                                            {sendResult.deliveryId}
                                        </div>
                                    </div>

                                    {"topicName" in sendResult && sendResult.topicName && (
                                        <div>
                                            <span className="text-sm text-gray-600 font-semibold">Topic:</span>
                                            <div className="font-mono text-sm bg-slate-50 p-2 rounded mt-1">
                                                {sendResult.topicName}
                                            </div>
                                        </div>
                                    )}

                                    {sendResult.providerMessageId && (
                                        <div>
                                            <span className="text-sm text-gray-600 font-semibold">Provider Message ID:</span>
                                            <div className="font-mono text-sm bg-slate-50 p-2 rounded mt-1">
                                                {sendResult.providerMessageId}
                                            </div>
                                        </div>
                                    )}

                                    {sendResult.errorMessage && (
                                        <div className="p-2 bg-red-50 rounded border border-red-200">
                                            <span className="text-sm font-semibold text-red-700">Error:</span>
                                            <div className="text-sm text-red-700 mt-1">
                                                {sendResult.errorMessage}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </AppCard.Body>
                        </AppCard>
                    )}

                    {/* Help Panel */}
                    {!previewResult && !sendResult && (
                        <AppCard className="bg-blue-50/50">
                            <AppCard.Body>
                                <div className="text-blue-800">
                                    <div className="font-semibold mb-2">
                                        <i className="pi pi-info-circle mr-2" />
                                        How to send a notification
                                    </div>
                                    <ol className="list-decimal list-inside space-y-1 text-sm">
                                        <li>Select the event type (e.g., PAYMENT_RECEIVED)</li>
                                        <li>Choose the delivery channel (Email, SMS, Push, Pull)</li>
                                        <li>Enter the recipient (email, phone, or User ID depending on channel)</li>
                                        <li>Optionally select a language for localization</li>
                                        <li>Fill in the payload JSON with required variables</li>
                                        <li>Click &quot;Preview&quot; to see how the message will look</li>
                                        <li>Click &quot;Send&quot; to deliver the notification</li>
                                    </ol>
                                </div>
                            </AppCard.Body>
                        </AppCard>
                    )}
                </div>
            </div>
        </>
    );
};

export default SendNotification;
